import type { PlatformBridge } from './PlatformBridge';
import { webBridge } from './web';
import { androidBridge } from './android';
import { iosBridge } from './ios';
import { iosClipBridge } from './iosClip';

export type PlatformId = PlatformBridge['platform'];

/**
 * Query marker baked into the Android TWA's launch URL (android-twa/app/build.gradle
 * `twaManifest.launchUrl`, mirrored in android-twa/twa-manifest.json `startUrl`).
 */
export const TWA_LAUNCH_PARAM = 'platform';

/**
 * Referrer Chrome puts on the TWA's launch navigation. Matched in full, not by the
 * `android-app://` scheme alone: Chrome sets that same scheme for any link handed to it by
 * another app, so a plain browser visit opened from a chat app would otherwise be read as our
 * paid app and lose its store badges. Must stay in sync with `applicationId` in
 * android-twa/app/build.gradle.
 */
const TWA_REFERRER = 'android-app://app.abcshooter.twa';

/**
 * Where a non-web verdict is latched for the rest of the app session.
 *
 * Both TWA signals below exist only on the *launch* navigation, so a reload inside the app would
 * otherwise look like an ordinary browser visit and the free-web store badges would appear in
 * the paid app. Deliberately sessionStorage and not localStorage: a TWA is Chrome, and it shares
 * the origin's localStorage with the browser on the same device — a persisted verdict would go
 * on suppressing the badges when the user later opens abcshooter.web.app in Chrome itself.
 * sessionStorage is per-tab, and the TWA is its own tab.
 */
const LATCH_KEY = 'abcs.platform.v1';

/** Everything detection looks at, passed in so the rules can be tested without a DOM. */
export interface HostSignals {
  /** navigator.userAgent */
  ua: string;
  /** window.__ABCS_PLATFORM__ — set by the iOS shell before any page JS runs. */
  injected?: string;
  /** document.referrer — `android-app://<package>` when Chrome launched us as a TWA. */
  referrer: string;
  /** location.search — the TWA's launch URL carries the marker above. */
  search: string;
  /** Verdict latched earlier in this app session, if any. */
  latched?: string | null;
}

/**
 * Which host we are running in. Gets the store badges and the buy card right (web only, see
 * src/game/promo.ts) and the rating prompt right (paid apps only, see src/game/rating.ts).
 */
export function detectPlatformId(s: HostSignals): PlatformId {
  // iOS shell — a WKUserScript at document start, so this is already set when we run. The App
  // Clip injects its own value: same WebView code, but an unbought trial, so it must never be
  // mistaken for the paid app (no rating ask) nor for the web build (no Play badge).
  if (s.injected === 'ios') return 'ios';
  if (s.injected === 'ios-clip') return 'ios-clip';
  if (s.injected === 'android') return 'android';

  // Android TWA. A Trusted Web Activity is full Chrome in a Custom Tab, NOT a WebView: its user
  // agent is an ordinary Chrome-for-Android string with no `; wv)` marker, so the UA can tell us
  // nothing at all. Two launch-time signals can, and both are checked because they cover
  // different installs:
  //   • the marker we bake into the launch URL — deterministic, but only in builds that ship it;
  //   • the android-app:// referrer Chrome sets on a TWA's launch navigation — which also covers
  //     copies installed before the marker existed.
  if (new URLSearchParams(s.search).get(TWA_LAUNCH_PARAM) === 'android') return 'android';
  if (s.referrer === TWA_REFERRER || s.referrer.startsWith(`${TWA_REFERRER}/`)) return 'android';

  // Neither signal survives a reload inside the app — see LATCH_KEY.
  if (s.latched === 'android' || s.latched === 'ios' || s.latched === 'ios-clip') return s.latched;

  // A plain Android WebView. Not one of the shipped shells, but the old hint costs nothing.
  if (/Android/i.test(s.ua) && /; wv\)/.test(s.ua)) return 'android';

  return 'web';
}

const BRIDGES: Record<PlatformId, PlatformBridge> = {
  web: webBridge,
  android: androidBridge,
  ios: iosBridge,
  'ios-clip': iosClipBridge,
};

let bridge: PlatformBridge = webBridge;

function readLatch(): string | null {
  try {
    return sessionStorage.getItem(LATCH_KEY);
  } catch {
    return null;   // storage blocked — the launch signals still work, reloads just lose them
  }
}

export function initPlatform(): void {
  const injected = (window as unknown as { __ABCS_PLATFORM__?: string }).__ABCS_PLATFORM__;
  const id = detectPlatformId({
    ua: navigator.userAgent,
    injected,
    referrer: document.referrer,
    search: location.search,
    latched: readLatch(),
  });

  // Only a paid-app verdict is worth latching; 'web' is the fallback anyway.
  if (id !== 'web') {
    try {
      sessionStorage.setItem(LATCH_KEY, id);
    } catch { /* storage blocked — a reload inside the app falls back to the launch signals */ }
  }

  bridge = BRIDGES[id];
}

export function platform(): PlatformBridge {
  return bridge;
}

export type { PlatformBridge } from './PlatformBridge';
